import {
  getInseeCodeFromLegalForm,
  LEGAL_FORMS,
} from '@shinetools/legal-form-library';
import { z } from 'zod';

import { formatAmount } from 'common/formatAmount';
import zodLocales from 'common/zod/locales';

import locales from '../pages/CapitalInfo/locales';

const MINIMUM_LEGAL_SHARE_CAPITAL_AMOUNT_IN_EUROS = 1;
const MINIMUM_NUMBER_OF_SHARES = 1;

export const getCapitalInfoSchema = (inseeLegalFormCode: string | null) =>
  z
    .object({
      legalNumberOfShares: z
        .number({ invalid_type_error: zodLocales.errors.required })
        .int(locales.legalNumberOfShares.invalidError)
        .min(
          MINIMUM_NUMBER_OF_SHARES,
          locales.formatString(locales.legalNumberOfShares.minValueError, {
            minimumNumber: MINIMUM_NUMBER_OF_SHARES,
          }),
        ),
      legalShareCapital: z
        .number({ invalid_type_error: zodLocales.errors.required })
        .int(locales.legalShareCapital.invalidError)
        .min(
          MINIMUM_LEGAL_SHARE_CAPITAL_AMOUNT_IN_EUROS,
          locales.formatString(locales.legalShareCapital.minValueError, {
            minimumAmount: formatAmount({
              forcePlusSign: false,
              value: MINIMUM_LEGAL_SHARE_CAPITAL_AMOUNT_IN_EUROS,
              withNoMinorUnitDigits: true,
            }),
          }),
        ),
      paidUpCapitalPercentage: z
        .number({
          invalid_type_error: locales.paidUpCapitalPercentage.numberError,
        })
        .int(locales.paidUpCapitalPercentage.integerError)
        .min(1, locales.paidUpCapitalPercentage.rangeError)
        .max(100, locales.paidUpCapitalPercentage.rangeError)
        .optional(),
      paidUpCapitalPercentageDefault: z.union([z.string(), z.number()]),
    })
    .superRefine(
      ({ paidUpCapitalPercentage, paidUpCapitalPercentageDefault }, ctx) => {
        if (
          paidUpCapitalPercentageDefault === 'other' &&
          paidUpCapitalPercentage
        ) {
          if (
            (inseeLegalFormCode ===
              getInseeCodeFromLegalForm(LEGAL_FORMS.SARL) ||
              inseeLegalFormCode ===
                getInseeCodeFromLegalForm(LEGAL_FORMS.SELARL) ||
              inseeLegalFormCode ===
                getInseeCodeFromLegalForm(LEGAL_FORMS.SC)) &&
            paidUpCapitalPercentage < 20
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                locales.paidUpCapitalPercentage.rangeErrorAboveTwentyPercent,
              path: ['paidUpCapitalPercentage'],
            });
          }
          if (
            inseeLegalFormCode === getInseeCodeFromLegalForm(LEGAL_FORMS.SAS) &&
            paidUpCapitalPercentage < 50
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                locales.paidUpCapitalPercentage.rangeErrorAboveFiftyPercent,
              path: ['paidUpCapitalPercentage'],
            });
          }
        }
      },
    );

export type CapitalInfoSchema = z.infer<
  ReturnType<typeof getCapitalInfoSchema>
>;
